import React from 'react';

import { faClock } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { UseFormReturn } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';
import { useHookFormMask } from 'use-mask-input';

import SettingsContext from 'src/features/administration/context/SettingsContext';
import { useGetPricingPlan } from 'src/features/pricingPlan/api/useGetPricingPlan';
import { CURRENCY_MAP, DAY_OF_WEEK_TRANSLATIONS_MAP } from 'src/features/pricingPlan/utils/constants';
import { PricingPlanTickRateFormSchemaValues } from 'src/features/pricingPlan/utils/formSchema';
import { pricingPlanMessages } from 'src/features/pricingPlan/utils/messages';
import { DAY_OF_WEEK } from 'src/features/pricingPlan/utils/types';
import { useParking } from 'src/providers/ParkingProvider';
import SelectFormField from 'src/registry/components/forms/SelectFormField';
import { Form, FormControl, FormControlStack, FormItem, FormLabel, FormMessage, FormField } from 'src/registry/ui/Form';
import { Input } from 'src/registry/ui/Input';
import {
    MultiSelect,
    MultiSelectContent,
    MultiSelectControl,
    MultiSelectOption,
    MultiSelectTrigger,
} from 'src/registry/ui/MultiSelect';

type TickRateFormProps = {
    form: UseFormReturn<PricingPlanTickRateFormSchemaValues>;
    onSubmit: (values: PricingPlanTickRateFormSchemaValues) => void;
    isCreate: boolean;
};

function TickRateForm({ onSubmit, form, isCreate }: TickRateFormProps) {
    const { formatMessage } = useIntl();
    const [displayRate, setDisplayRate] = React.useState('');
    const { planId = '' } = useParams();
    const { parkingId } = useParking();
    const { data: pricingPlan } = useGetPricingPlan({ planId, parkingId });
    const { default_currency } = React.useContext(SettingsContext);

    const currency = React.useMemo(
        () => (pricingPlan?.currency ? formatMessage(CURRENCY_MAP[pricingPlan.currency]) : default_currency),
        [default_currency, formatMessage, pricingPlan],
    );
    const registerWithMask = useHookFormMask(form.register);

    return (
        <Form form={form} onSubmit={form.handleSubmit(onSubmit)} variant="dialog">
            {isCreate ? (
                <FormField
                    control={form.control}
                    name="day_of_week"
                    render={({ field }) => {
                        const { ref: _, ...rest } = field;
                        const daysOfWeekArray = Array.isArray(field.value) ? field.value : [];
                        const fieldData = daysOfWeekArray
                            ?.map((identifier: DAY_OF_WEEK) => {
                                const [, value] = Object.entries(DAY_OF_WEEK_TRANSLATIONS_MAP).find(
                                    ([key]) => (key as DAY_OF_WEEK) === identifier,
                                )!;

                                return value ? formatMessage(value) : null;
                            })
                            .join(', ');

                        const allValues: DAY_OF_WEEK[] = Object.entries(DAY_OF_WEEK_TRANSLATIONS_MAP).map(
                            ([key]) => key as DAY_OF_WEEK,
                        );
                        return (
                            <FormItem variant="dialog">
                                <FormLabel variant="dialog" required>
                                    <FormattedMessage {...pricingPlanMessages.day} />
                                </FormLabel>
                                <FormControlStack>
                                    <FormControl>
                                        <MultiSelect {...rest}>
                                            <MultiSelectTrigger asChild>
                                                <MultiSelectControl className="-mr-4 w-full">
                                                    {fieldData}
                                                    {fieldData?.length == 0 && (
                                                        <span className="text-gray-400">
                                                            {`${formatMessage(pricingPlanMessages.day)}...`}
                                                        </span>
                                                    )}
                                                </MultiSelectControl>
                                            </MultiSelectTrigger>
                                            <MultiSelectContent allValues={allValues}>
                                                {Object.entries(DAY_OF_WEEK_TRANSLATIONS_MAP).map(([key, value]) => (
                                                    <MultiSelectOption key={key} selected={key}>
                                                        <div className="flex flex-row justify-between">
                                                            {formatMessage(value)}
                                                        </div>
                                                    </MultiSelectOption>
                                                ))}
                                            </MultiSelectContent>
                                        </MultiSelect>
                                    </FormControl>
                                    <FormMessage />
                                </FormControlStack>
                            </FormItem>
                        );
                    }}
                />
            ) : (
                <SelectFormField<PricingPlanTickRateFormSchemaValues>
                    form={form}
                    name="day_of_week"
                    label={formatMessage(pricingPlanMessages.day)}
                    options={Object.values(DAY_OF_WEEK)}
                    optionTranslationMap={DAY_OF_WEEK_TRANSLATIONS_MAP}
                />
            )}
            <FormField
                control={form.control}
                name="rate_with_tax"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog" required>
                            <FormattedMessage {...pricingPlanMessages.currencyRate} values={{ currency }} />
                        </FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <Input
                                    type="text"
                                    inputMode="decimal"
                                    placeholder={`${formatMessage(pricingPlanMessages.currencyRate, { currency })}...`}
                                    value={
                                        displayRate !== ''
                                            ? displayRate
                                            : field.value != null
                                              ? (field.value / 100).toFixed(2)
                                              : ''
                                    }
                                    onChange={(e) => {
                                        const inputValue = e.target.value;

                                        if (!/^\d*\.?\d{0,2}$/.test(inputValue)) return;

                                        setDisplayRate(inputValue);

                                        const numeric = parseFloat(inputValue);
                                        if (!isNaN(numeric)) {
                                            field.onChange(Math.round(numeric * 100));
                                        } else {
                                            field.onChange(undefined);
                                        }
                                    }}
                                    onBlur={() => {
                                        if (displayRate === '' || isNaN(parseFloat(displayRate))) return;
                                        const formatted = parseFloat(displayRate).toFixed(2);
                                        setDisplayRate(formatted);
                                        field.onChange(Math.round(parseFloat(formatted) * 100));
                                    }}
                                    onKeyDown={(e) => {
                                        if (['-', '+', 'e'].includes(e.key)) e.preventDefault();
                                    }}
                                />
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />

            <FormField
                control={form.control}
                name="from_time"
                render={() => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog" required>
                            <FormattedMessage {...pricingPlanMessages.fromHour} />
                        </FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <div className="relative flex min-w-32 items-center gap-2">
                                    <FontAwesomeIcon
                                        icon={faClock}
                                        size="sm"
                                        className="text-muted-foreground pointer-events-none absolute top-1/2 left-2 -translate-y-1/2"
                                    />
                                    <Input
                                        type="text"
                                        placeholder="00-23:59"
                                        inputMode="numeric"
                                        className="pl-8 text-sm"
                                        {...registerWithMask('from_time', 'datetime', {
                                            inputFormat: 'HH:mm',
                                            outputFormat: 'HH:mm',
                                            showMaskOnHover: false,
                                            placeholder: '0',
                                        })}
                                    />
                                </div>
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />
            <FormField
                control={form.control}
                name="to_time"
                render={() => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog" required>
                            <FormattedMessage {...pricingPlanMessages.toHour} />
                        </FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <div className="relative flex w-full min-w-32 items-center gap-2">
                                    <FontAwesomeIcon
                                        icon={faClock}
                                        size="sm"
                                        className="text-muted-foreground pointer-events-none absolute top-1/2 left-2 -translate-y-1/2"
                                    />
                                    <Input
                                        type="text"
                                        placeholder="00-23:59"
                                        inputMode="numeric"
                                        className="pl-8 text-sm"
                                        {...registerWithMask('to_time', 'datetime', {
                                            inputFormat: 'HH:mm',
                                            outputFormat: 'HH:mm',
                                            showMaskOnHover: false,
                                            placeholder: '0',
                                        })}
                                    />
                                </div>
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />
        </Form>
    );
}

export default TickRateForm;
